import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { MapPin, Compass, Tractor } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ExploreMode } from "./ExploreModeToggle";

interface ExploreFooterNavProps {
  /** Current active mode — pass null to show no active state (e.g. on /favoriter) */
  mode?: ExploreMode | null;
  /** If provided, call this instead of navigating (used inside ExploreMobile) */
  onModeChange?: (mode: ExploreMode) => void;
}

const TABS: { key: ExploreMode; label: string; icon: React.ElementType }[] = [
  { key: "places", label: "Platser", icon: MapPin },
  { key: "do", label: "Göra", icon: Compass },
  { key: "farms", label: "Gårdar", icon: Tractor },
];

/** Derive active mode from URL when no explicit mode prop is given */
function modeFromUrl(pathname: string, params: URLSearchParams): ExploreMode | null {
  if (!pathname.startsWith("/explore")) return null;
  const m = params.get("mode");
  if (m === "do" || m === "farms") return m;
  return "places";
}

/**
 * Sticky bottom navigation for mobile Explore.
 * Supports both callback mode (inside ExploreMobile) and navigation mode (standalone).
 * Active state is always route-driven to stay in sync after back/forward.
 */
export function ExploreFooterNav({ mode: modeProp, onModeChange }: ExploreFooterNavProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  // Route-driven: use URL as source of truth, allow prop override
  const activeMode = modeProp !== undefined ? modeProp : modeFromUrl(pathname, searchParams);

  const handleClick = (key: ExploreMode) => {
    if (import.meta.env.DEV || import.meta.env.VITE_EXPLORE_DEBUG === "true") console.log("[ExploreFooterNav] click", key, "current:", activeMode);
    if (onModeChange) {
      onModeChange(key);
    } else {
      const param = key === "places" ? "" : `?mode=${key}`;
      navigate(`/explore${param}`);
    }
  };

  return (
    <nav
      data-testid="explore-footer-nav"
      className="fixed bottom-0 inset-x-0 z-[1000] bg-background/95 backdrop-blur-md border-t border-border md:hidden pointer-events-auto"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Utforska-navigation"
    >
      <div className="flex items-stretch">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = activeMode === key;
          return (
            <button
              key={key}
              onClick={() => handleClick(key)}
              className={cn(
                "relative flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px] transition-all",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
              aria-current={active ? "page" : undefined}
            >
              {/* Active pill background */}
              {active && (
                <span className="absolute inset-x-3 inset-y-1 rounded-xl bg-primary/10 pointer-events-none" />
              )}
              <Icon
                className={cn(
                  "h-5 w-5 relative z-10 transition-transform",
                  active && "stroke-[2.5] scale-110"
                )}
              />
              <span
                className={cn(
                  "text-[11px] leading-tight relative z-10",
                  active ? "font-bold" : "font-medium"
                )}
              >
                {label}
              </span>
              {/* Active underline dot */}
              <span
                className={cn(
                  "h-1 w-5 rounded-full relative z-10 transition-opacity",
                  active ? "bg-primary opacity-100" : "opacity-0"
                )}
              />
            </button>
          );
        })}
      </div>
    </nav>
  );
}
